import {MimeType} from "@reva/shared/mime-type";
import {hasSpfTable, normalizeSpf, SPF_PARAMS} from "@reva/shared/parameter-resolution";
import {SECTION_II_ENTRY_PARAMS, sectionIIRowsFromEntryParams} from "@reva/shared/register";

import type ServiceApi from "../api";
import type {ApiCredentials} from "../api/credentials";
import {isAbortError} from "../api/executor";
import {DocumentUploadApiInterface} from "../api/interface/documents";
import {GeoRefApiInterface} from "../api/interface/georef";
import {type ImmoDropResult, ImmodropFileApiInterface, ImmodropUrlApiInterface} from "../api/interface/immodrop";
import type {AppEnvironment} from "../environment";
import {generateDefaultQueryError} from "../util/errorUtil";
import {buildImmodropSource, IMMODROP_SOURCE_KEY} from "../util/immodropSource";
import {selectCredentials} from "./authSlice";
import {loadDocumentsAsync} from "./documentsThunk";
import {
    clear,
    selectAddressQuery,
    selectImmodropDocumentId,
    selectImmodropFilename,
    selectImmodropUrl,
    selectImportAddress,
    selectImportJson,
    selectImportObjectParams,
    selectImportSegment,
    selectValid,
    setAddressError,
    setAddressFound,
    setAddressLoading,
    setImmodropDocumentId,
    setImmodropError,
    setImmodropLoading,
    setImportJsonData,
    setJsonInput,
    setJsonParsingErrorI18n,
} from "./importSlice";
import {
    selectAllInspectionDerivedParams,
    selectAllRegisterParams,
    setParamsForParameterType,
    setSegmentAndParameters,
    updateParamsForParameterType,
} from "./objectParametersSlice";
import {validateAndSubmitThunk} from "./objectParametersThunk";
import {selectRevaId} from "./persistenceSlice";
import {initializePersistence, persistMultipleParameters} from "./persistenceThunk";
import type {RevaThunkAction} from "./revaHooks";
import type {RevaDispatch, RevaRootState} from "./store";

const IMPORT_TO_REGISTER_PARAMS = [
    ...SECTION_II_ENTRY_PARAMS,
    "prop_leasehold",
    "prop_leasehold_amount_pa",
    "prop_leasehold_duration",
    "prop_leasehold_interest_rate",
    "prop_leasehold_interest_rate_ground",
    "prop_leasehold_general_disadvantage",
    "prop_leasehold_building_compensation",
];
const IMPORT_TO_INSPECTION_DERIVED_PARAMS = ["gross_floor_area", "prop_pool", "prop_sauna"];
const SPF_PARAM_KEYS = new Set<string>(Object.values(SPF_PARAMS));
const SPF_FLAGS = {prop_pool: "Pool", prop_sauna: "Sauna"} as const;

/**
 * Merkmal-Tabelle aus dem Import: normalisiert auf die Länge der Namen (Werte ohne Namen werden verworfen),
 * dazu Pool/Sauna aus prop_pool/prop_sauna, sofern nicht schon als Merkmal vorhanden.
 */
function spfFromImport(importObjectParams: Record<string, unknown>): Record<string, unknown[]> {
    const importedNames = importObjectParams[SPF_PARAMS.names];
    const spf = (Array.isArray(importedNames) && importedNames.length > 0 && normalizeSpf(importObjectParams)) || {
        [SPF_PARAMS.names]: [],
        [SPF_PARAMS.values]: [],
    };
    const names = spf[SPF_PARAMS.names] as unknown[];

    for (const [flag, feature] of Object.entries(SPF_FLAGS)) {
        const present = names.some((name) => typeof name === "string" && name.toLowerCase() === feature.toLowerCase());
        if (!importObjectParams[flag] || present) continue;
        for (const [param, values] of Object.entries(spf)) values?.push(param === SPF_PARAMS.names ? feature : null);
    }
    return names.length
        ? (Object.fromEntries(Object.entries(spf).filter(([, values]) => values != null)) as Record<string, unknown[]>)
        : {};
}

//schreibt Array-parameter, da sie keine Konfliktauflösung haben, direkt in ihre Schichten
function buildRegisterAndInspectionDerivedFromImport(importObjectParams: Record<string, unknown>): {
    register: Record<string, unknown>;
    inspectionDerived: Record<string, unknown>;
} {
    const register = IMPORT_TO_REGISTER_PARAMS.reduce<Record<string, unknown>>((acc, param) => {
        acc[param] = importObjectParams[param];
        return acc;
    }, {});
    register["register_abt2"] = sectionIIRowsFromEntryParams(register);

    // prop_pool/prop_sauna gehen als Merkmal in die Tabelle und werden dann nicht zusätzlich gesetzt
    const inspectionDerived: Record<string, unknown> = Object.fromEntries(
        IMPORT_TO_INSPECTION_DERIVED_PARAMS.filter((param) => !(param in SPF_FLAGS && importObjectParams[param])).map(
            (param) => [param, importObjectParams[param]],
        ),
    );
    Object.assign(inspectionDerived, spfFromImport(importObjectParams));

    return {register, inspectionDerived};
}

// register_abt2 is always an array, `[]` when the import found nothing for it (unlike the other
// fields here, which end up `undefined` in that case and are already caught above).
function hasImportedValue(value: unknown): boolean {
    if (value === undefined) return false;
    return !(Array.isArray(value) && value.length === 0);
}

function onlyUnsetParams(
    candidate: Record<string, unknown>,
    current: object | null | undefined,
): Record<string, unknown> {
    const currentValues = (current ?? {}) as Record<string, unknown>;
    return Object.fromEntries(
        Object.entries(candidate).filter(([key, value]) => hasImportedValue(value) && currentValues[key] == null),
    );
}

// AbortController des laufenden Exposé-Imports (Extraktion + anschließende Adressauflösung), damit die
// Benutzerin ihn über den Fortschrittsdialog abbrechen kann (#19159). Ein globaler Guard genügt, da immer
// nur ein Import gleichzeitig läuft — analog valueIndicationAbortController in valueIndicationThunk.ts.
let immodropAbortController: AbortController | null = null;

/**
 * Startet einen neuen Import-Lauf und bricht einen eventuell noch laufenden ab.
 * @return der Controller des neuen Laufs, gegen den nach jedem await die Identität geprüft wird
 */
function _startImmodropRun(): AbortController {
    immodropAbortController?.abort();
    const controller = new AbortController();
    immodropAbortController = controller;
    return controller;
}

/**
 * Gehört die gerade eingetroffene Antwort noch zum aktuellen Lauf?
 *
 * Zweite Absicherung neben dem AbortSignal: Die Adressauflösung hängt hinter der Extraktion, ein Abbruch
 * kann also zwischen zwei awaits greifen — dann darf das Ergebnis den Store nicht mehr anfassen.
 * @param controller der Controller des eigenen Laufs
 */
function _isCurrentImmodropRun(controller: AbortController): boolean {
    return immodropAbortController === controller && !controller.signal.aborted;
}

/**
 * Bricht den laufenden Exposé-Import ab (Fortschrittsdialog, #19159).
 *
 * Bricht die laufende HTTP-Anfrage wirklich ab (AbortSignal) und verwirft den angefangenen Import samt
 * der noch nicht archivierten Datei. Ein bereits gestarteter Serverlauf wird davon nicht zurückgenommen,
 * aber sein Ergebnis erreicht REVA nicht mehr.
 */
export const abortImmodropImportAsync = (): RevaThunkAction<void> => (dispatch) => {
    immodropAbortController?.abort();
    immodropAbortController = null;
    dispatch(discardImportAsync());
};

/**
 * Shared response handler for both URL-based and file-based ImmoDrop extraction.
 * @param controller Controller des laufenden Imports, für Abbruch-Signal und Identitätsprüfung
 */
async function _finalizeImmodropResponse(
    importJson: ImmoDropResult,
    dispatch: RevaDispatch,
    getState: () => RevaRootState,
    env: AppEnvironment,
    controller?: AbortController,
): Promise<void> {
    dispatch(setImportJsonData({importJson}));
    if (env.isDevelopmentMode) {
        console.log(`Successful Immodrop response:`, importJson);
    }
    // Immediately start to geo-reference detected address ...
    const addressQuery = selectAddressQuery(getState()) ?? "";
    await dispatch(queryImportAddressAsync(addressQuery, controller));
}

/**
 * Segment, das die Extraktion geliefert hat. Bewusst nicht importSegment: das ist mit dem Objekttyp
 * vorbelegt und im Import-Dialog von Hand änderbar, beschreibt also nicht mehr das Exposé (#19159).
 */
function extractedSegment(importJson: Record<string, unknown> | null | undefined): string | null {
    const segment = importJson?.segment;
    return typeof segment === "string" && segment ? segment : null;
}

/**
 * Das zuletzt hochgeladene Exposé-PDF, solange der Import noch nicht bestätigt ist.
 *
 * Liegt bewusst neben dem Store: eine File gehört nicht in den Redux-State (nicht serialisierbar), und
 * archiviert wird erst beim Übernehmen — ein in der Auftragsakte abgelegtes Dokument bekommt man dort
 * nicht wieder heraus (#19159).
 */
let pendingExposeFile: File | null = null;

/**
 * Dokumenttyp, unter dem das Exposé in der Value-Orders-API abgelegt wird. Einen eigenen Exposé-Typ
 * gibt es dort nicht; InfoDocument ist der neutrale Informationsbeleg zum Objekt (#19159).
 */
const EXPOSE_DOCUMENT_TYPE = "InfoDocument";

/** Fester Name des archivierten Exposés, damit es in der Auftragsakte wiedererkennbar ist. */
const EXPOSE_DOCUMENT_FILENAME = "Expose.pdf";

/**
 * Legt das hochgeladene Exposé zusätzlich in der Value-Orders-API ab, damit man es später noch ansehen kann.
 *
 * Nebensache des Imports: ohne verknüpften Auftrag oder bei schon vergebenem Dateinamen lehnt die API ab —
 * das darf den Import nicht scheitern lassen, deshalb wird der Fehler nur geloggt. Bei Namenskollision folgt
 * ein zweiter Versuch mit Zeitstempel im Namen.
 * @param file das hochgeladene PDF
 * @param revaId die Bewertung
 * @param serviceApi API-Zugriff
 * @param credentials Zugangsdaten
 * @return die Dokument-Id in der Orders-API, oder null wenn das Archivieren nicht möglich war
 */
async function archiveExposeInValueOrders(
    file: File,
    revaId: string,
    serviceApi: ServiceApi,
    credentials: ApiCredentials,
): Promise<string | null> {
    const upload = async (filename: string): Promise<string> => {
        const renamed = new File([file], filename, {type: file.type || MimeType.PDF});
        const {documentId} = await new DocumentUploadApiInterface(revaId, EXPOSE_DOCUMENT_TYPE, renamed).execute(
            serviceApi,
            credentials,
        );
        return documentId;
    };
    try {
        return await upload(EXPOSE_DOCUMENT_FILENAME);
    } catch (err) {
        console.warn("Could not archive exposé in the Orders API, retrying with a unique filename", err);
    }
    try {
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        return await upload(`Expose-${stamp}.pdf`);
    } catch (err) {
        console.warn("Could not archive exposé in the Orders API", err);
        return null;
    }
}

/**
 * Async thunk to submit a URL to Immodrop.
 * @param targetUrl target URL to send to ImmoDrop
 */
export const queryImmodropAsync =
    (targetUrl: string): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {env, serviceApi} = extraArgument;
        pendingExposeFile = null;
        dispatch(setImmodropLoading({targetUrl}));
        const controller = _startImmodropRun();
        const apiInterface = new ImmodropUrlApiInterface(targetUrl, undefined, controller.signal);
        if (!apiInterface.validateAndDispatchQueryError((queryError) => dispatch(setImmodropError(queryError)))) {
            return;
        }
        const credentials = selectCredentials(getState());
        try {
            if (env.isDevelopmentMode) {
                console.log(`Immodrop query for url: ${targetUrl}`);
            }
            const importJson = await apiInterface.execute(serviceApi, credentials!);
            if (!_isCurrentImmodropRun(controller)) return;
            await _finalizeImmodropResponse(importJson, dispatch, getState, env, controller);
        } catch (err) {
            // Vom Benutzer abgebrochen: erwartbar, still schlucken — abortImmodropImportAsync hat schon aufgeräumt.
            if (isAbortError(err)) return;
            dispatch(setImmodropError(generateDefaultQueryError(err as Error)));
        }
    };

/**
 * Async thunk to upload a PDF file to Immodrop.
 * @param file PDF file to upload
 */
export const queryImmodropFileAsync =
    (file: File): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {env, serviceApi} = extraArgument;
        dispatch(setImmodropLoading({targetUrl: null, targetFilename: file.name}));
        const controller = _startImmodropRun();
        const credentials = selectCredentials(getState());
        try {
            if (env.isDevelopmentMode) {
                console.log(`Immodrop file upload: ${file.name}`);
            }
            const apiInterface = new ImmodropFileApiInterface(file, controller.signal);
            const importJson = await apiInterface.execute(serviceApi, credentials!);
            if (!_isCurrentImmodropRun(controller)) return;
            pendingExposeFile = file;
            await _finalizeImmodropResponse(importJson, dispatch, getState, env, controller);
        } catch (err) {
            // Vom Benutzer abgebrochen: erwartbar, still schlucken — abortImmodropImportAsync hat schon aufgeräumt.
            if (isAbortError(err)) return;
            dispatch(setImmodropError(generateDefaultQueryError(err as Error)));
        }
    };

/**
 * Async thunk to submit a JSON source to import.
 * @param jsonInput target JSON input
 * @return true if the JSON input was valid and the import was started
 */
export const queryJsonInputAsync =
    (jsonInput: string): RevaThunkAction<Promise<boolean>> =>
    async (dispatch, getState, extraArgument) => {
        const {env} = extraArgument;
        dispatch(setJsonInput({jsonInput}));
        if (!jsonInput) {
            dispatch(setJsonParsingErrorI18n({errorI18n: "importDialog.json.error.empty"}));
            return false;
        }
        let jsonParsed: Record<string, unknown> | undefined;
        try {
            jsonParsed = JSON.parse(jsonInput);
        } catch {
            dispatch(
                setJsonParsingErrorI18n({
                    errorI18n: "importDialog.json.error.parsing",
                }),
            );
            return false;
        }
        if (!jsonParsed || typeof jsonParsed !== "object" || Array.isArray(jsonParsed)) {
            dispatch(
                setJsonParsingErrorI18n({
                    errorI18n: "importDialog.json.error.malformed",
                }),
            );
            return false;
        }
        if (!jsonParsed.segment) {
            dispatch(
                setJsonParsingErrorI18n({
                    errorI18n: "importDialog.json.error.missingSegment",
                }),
            );
            return false;
        }
        if (!jsonParsed.address) {
            dispatch(
                setJsonParsingErrorI18n({
                    errorI18n: "importDialog.json.error.missingAddress",
                }),
            );
            return false;
        }
        const geoRefInterface = new GeoRefApiInterface(jsonParsed.address as string);
        if (
            !geoRefInterface.validateAndDispatchError((error) => dispatch(setJsonParsingErrorI18n({errorI18n: error})))
        ) {
            return false;
        }
        if (env.isDevelopmentMode) {
            console.log("Import object parameters from JSON:", jsonParsed);
        }
        dispatch(setImportJsonData({importJson: jsonParsed}));

        // Immediately start to geo-reference detected address ...
        const addressQuery = selectAddressQuery(getState()) ?? "";
        await dispatch(queryImportAddressAsync(addressQuery));

        return true;
    };

/**
 * Löst die im Import gefundene Adresse geografisch auf.
 * @param addressQuery die gefundene Adresse
 * @param controller Controller des laufenden Exposé-Imports, sofern der Aufruf zu einem gehört (#19159)
 */
export const queryImportAddressAsync =
    (addressQuery: string, controller?: AbortController): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {env, serviceApi} = extraArgument;
        const geoRefInterface = new GeoRefApiInterface(addressQuery, null, undefined, controller?.signal);
        if (!geoRefInterface.validateAndDispatchQueryError((queryError) => dispatch(setAddressError(queryError)))) {
            return;
        }
        dispatch(setAddressLoading({addressQuery}));
        const credentials = selectCredentials(getState());
        try {
            if (env.isDevelopmentMode) {
                console.log(`GeoRef query (during import) for address: ${addressQuery}`);
            }
            const result = await geoRefInterface.execute(serviceApi, credentials!);
            if (controller && !_isCurrentImmodropRun(controller)) return;
            if (result.ambiguous) {
                dispatch(
                    setAddressError({
                        error: "address.error.multipleFound",
                        errorHasContactLink: false,
                    }),
                );
            } else {
                dispatch(setAddressFound({addressMeta: result.addressMeta}));
            }
        } catch (err) {
            if (isAbortError(err)) return;
            dispatch(setAddressError(generateDefaultQueryError(err as Error)));
        }
    };

export const finalizeImport = (): RevaThunkAction<Promise<void>> => async (dispatch, getState, extraArgument) => {
    const {env} = extraArgument;
    const valid = selectValid(getState());
    if (!valid) {
        return;
    }

    const importAddress = selectImportAddress(getState());
    const importSegment = selectImportSegment(getState());
    const importObjectParams = selectImportObjectParams(getState()) ?? {};
    if (env.isDevelopmentMode) {
        const immodropUrl = selectImmodropUrl(getState());
        const immodropFilename = selectImmodropFilename(getState());
        if (typeof immodropUrl === "string") {
            console.log(
                `Finalizing import from ImmoDrop URL ${immodropUrl}`,
                importAddress,
                importSegment,
                importObjectParams,
            );
        } else if (typeof immodropFilename === "string") {
            console.log(
                `Finalizing import from ImmoDrop file ${immodropFilename}`,
                importAddress,
                importSegment,
                importObjectParams,
            );
        } else {
            console.log("Finalizing import from JSON", importAddress, importSegment, importObjectParams);
        }
    }

    const {register: registerParamValues, inspectionDerived: inspectionParamValues} =
        buildRegisterAndInspectionDerivedFromImport(importObjectParams);

    dispatch(
        // Das Segment geht hier als REVA-Override in die Schicht, nicht als effektives Segment: die
        // Bewertung existiert noch nicht, es gibt also nichts, was das Backend berechnet haben könnte.
        // Genauso legt POST /valuations es ab -- das Top-Level-Segment landet dort in parametersReva.
        setSegmentAndParameters({
            params: {segment: importSegment},
            register: registerParamValues,
            inspectionDerived: inspectionParamValues,
            immodrop: {
                ...importObjectParams,
                [IMMODROP_SOURCE_KEY]: buildImmodropSource(
                    selectImmodropUrl(getState()),
                    selectImmodropFilename(getState()),
                    selectImmodropDocumentId(getState()),
                    extractedSegment(selectImportJson(getState())),
                ),
            },
        }),
    );

    // #18907: The machine-driven import flow (Immodrop/JSON import) deliberately does NOT show the
    // duplicate dialog — it creates directly (unchanged prior behavior, now a documented decision).
    await dispatch(initializePersistence(importAddress!, {skipDuplicateCheck: true}));
    // Clear old import data
    dispatch(clear());
};

/**
 * #19159: apply the currently extracted Immodrop/JSON import result (already loaded into
 * importSlice via {@link queryImmodropAsync}/{@link queryImmodropFileAsync}) to the CURRENTLY
 * loaded valuation instead of creating a new one. The address is intentionally never written here.
 */
export const applyImmodropImportToCurrentValuation =
    (): RevaThunkAction<Promise<void>> => async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const importObjectParams = selectImportObjectParams(getState()) ?? {};

        // Jetzt erst archivieren: vorher könnte die Benutzerin den Import wegen abweichender Adresse noch
        // abbrechen, und aus der Auftragsakte bekommt man das PDF nicht wieder heraus (#19159).
        const revaId = selectRevaId(getState());
        const credentials = selectCredentials(getState());
        if (pendingExposeFile && revaId && credentials) {
            const documentId = await archiveExposeInValueOrders(pendingExposeFile, revaId, serviceApi, credentials);
            pendingExposeFile = null;
            dispatch(setImmodropDocumentId({documentId}));
            if (documentId) {
                await dispatch(loadDocumentsAsync());
            }
        }

        const immodropParams = {
            ...importObjectParams,
            [IMMODROP_SOURCE_KEY]: buildImmodropSource(
                selectImmodropUrl(getState()),
                selectImmodropFilename(getState()),
                selectImmodropDocumentId(getState()),
                extractedSegment(selectImportJson(getState())),
            ),
        };

        dispatch(setParamsForParameterType({parameterType: "IMMODROP", params: immodropParams}));
        await dispatch(persistMultipleParameters(immodropParams, "IMMODROP"));

        const {register, inspectionDerived} = buildRegisterAndInspectionDerivedFromImport(importObjectParams);
        const newRegisterParams = onlyUnsetParams(register, selectAllRegisterParams(getState()));
        const currentInspectionDerived = selectAllInspectionDerivedParams(getState()) as Record<string, unknown> | null;
        // Merkmal-Arrays gehören zeilenweise zusammen: nur komplett übernehmen, nie in eine Tabelle mischen
        const newInspectionDerivedParams = onlyUnsetParams(
            hasSpfTable(currentInspectionDerived)
                ? Object.fromEntries(Object.entries(inspectionDerived).filter(([key]) => !SPF_PARAM_KEYS.has(key)))
                : inspectionDerived,
            currentInspectionDerived,
        );

        if (Object.keys(newRegisterParams).length > 0) {
            dispatch(updateParamsForParameterType({parameterType: "REGISTER", params: newRegisterParams}));
            await dispatch(persistMultipleParameters(newRegisterParams, "REGISTER"));
        }
        if (Object.keys(newInspectionDerivedParams).length > 0) {
            dispatch(
                updateParamsForParameterType({parameterType: "INSPECTION_DERIVED", params: newInspectionDerivedParams}),
            );
            await dispatch(persistMultipleParameters(newInspectionDerivedParams, "INSPECTION_DERIVED"));
        }

        // #19372: nach den Persists genau einmal neu rechnen, damit Marktwert und Energiewert dem importierten
        // Stand entsprechen. Unbedingt, weil die Immodrop-Schicht vollständig ersetzt wird.
        dispatch(validateAndSubmitThunk());

        dispatch(clear());
    };

/**
 * #19159: leert die Exposé-Schicht der geladenen Bewertung vollständig.
 *
 * Danach ist der Tab wieder leer und ein neuer Import (URL oder PDF) möglich. Ein bereits in der
 * Value-Orders-API abgelegtes Exposé-PDF bleibt dort liegen — die API kennt kein Löschen. Werte, die
 * beim Import in die Grundbuch- oder Besichtigungsschicht übernommen wurden, bleiben ebenfalls stehen;
 * sie gehören ab dann diesen Schichten.
 */
export const deleteImmodropLayerAsync = (): RevaThunkAction<Promise<void>> => async (dispatch) => {
    pendingExposeFile = null;
    dispatch(setParamsForParameterType({parameterType: "IMMODROP", params: {}}));
    await dispatch(persistMultipleParameters({}, "IMMODROP"));
    dispatch(validateAndSubmitThunk());
    dispatch(clear());
};

/**
 * Verwirft einen angefangenen Import samt der noch nicht archivierten Exposé-Datei (#19159).
 *
 * Für jeden Abbruch zu benutzen — das reine {@link clear} kennt die Datei nicht, sie würde sonst beim
 * nächsten Übernehmen mit in die Auftragsakte wandern.
 */
export const discardImportAsync = (): RevaThunkAction<void> => (dispatch) => {
    pendingExposeFile = null;
    dispatch(clear());
};
